import type { components } from '../../api/schema'
import { getLocalizedName } from '../../lib/localization'

export type AssetRelationType = components['schemas']['AssetRelationType']
export type CreateAssetRelationTypeRequest = components['schemas']['CreateAssetRelationTypeRequest']
export type PatchAssetRelationTypeRequest = components['schemas']['PatchAssetRelationTypeRequest']

export type Cardinality = 'one' | 'many'

/**
 * Editable shape shared by the create page and the detail/edit page. Kept as a
 * plain object (not react-hook-form) because every field is either a simple
 * scalar or an `{en, fr}` map the shared `MultilingualInput` already drives —
 * mirroring how the other ontology catalogs (compliance controls, aggregations)
 * build their forms.
 */
export interface RelationTypeFormState {
    code: string
    name: Record<string, string>
    description: Record<string, string>
    sourceRole: Record<string, string>
    targetRole: Record<string, string>
    color: string
    emoji: string
    sourceTypeCodes: string[]
    destTypeCodes: string[]
    aggregating: boolean
    sourceCardinality: Cardinality
    targetCardinality: Cardinality
    /** Kept as a string so the input can legitimately be empty (= null). */
    ordering: string
}

export function emptyRelationTypeForm(): RelationTypeFormState {
    return {
        code: '',
        name: { en: '', fr: '' },
        description: { en: '', fr: '' },
        sourceRole: { en: '', fr: '' },
        targetRole: { en: '', fr: '' },
        color: '',
        emoji: '',
        sourceTypeCodes: [],
        destTypeCodes: [],
        aggregating: false,
        sourceCardinality: 'many',
        targetCardinality: 'many',
        ordering: '',
    }
}

export function relationTypeToForm(rt: AssetRelationType): RelationTypeFormState {
    return {
        code: rt.code,
        name: { en: '', fr: '', ...(rt.name || {}) },
        description: { en: '', fr: '', ...(rt.description || {}) },
        sourceRole: { en: '', fr: '', ...(rt.source_role || {}) },
        targetRole: { en: '', fr: '', ...(rt.target_role || {}) },
        color: rt.color || '',
        emoji: rt.emoji || '',
        sourceTypeCodes: rt.possible_source_asset_type_codes || [],
        destTypeCodes: rt.possible_destination_asset_type_codes || [],
        aggregating: rt.aggregating,
        sourceCardinality: rt.source_cardinality,
        targetCardinality: rt.target_cardinality,
        ordering: rt.ordering === null || rt.ordering === undefined ? '' : String(rt.ordering),
    }
}

/** Drops empty locale entries so the API never stores `{"fr": ""}`. */
function compactI18n(value: Record<string, string>): Record<string, string> {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(value || {})) {
        if (v && v.trim()) out[k] = v.trim()
    }
    return out
}

function parseOrdering(value: string): number | null {
    const trimmed = (value || '').trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? Math.trunc(n) : null
}

export function formToCreateRequest(form: RelationTypeFormState): CreateAssetRelationTypeRequest {
    return {
        code: form.code.trim(),
        name: compactI18n(form.name),
        description: compactI18n(form.description),
        source_role: compactI18n(form.sourceRole),
        target_role: compactI18n(form.targetRole),
        color: form.color.trim() || null,
        emoji: form.emoji.trim() || null,
        possible_source_asset_type_codes: form.sourceTypeCodes.length ? form.sourceTypeCodes : null,
        possible_destination_asset_type_codes: form.destTypeCodes.length ? form.destTypeCodes : null,
        aggregating: form.aggregating,
        source_cardinality: form.sourceCardinality,
        target_cardinality: form.targetCardinality,
        ordering: parseOrdering(form.ordering),
    }
}

/**
 * Full-state PATCH body. The endpoint is tri-state (absent = untouched) but the
 * edit page always submits the whole form, so sending every field keeps the
 * "what you see is what is stored" guarantee — including clearing a value back
 * to null.
 */
export function formToPatchRequest(form: RelationTypeFormState): PatchAssetRelationTypeRequest {
    return {
        name: compactI18n(form.name),
        description: compactI18n(form.description),
        source_role: compactI18n(form.sourceRole),
        target_role: compactI18n(form.targetRole),
        color: form.color.trim() || null,
        emoji: form.emoji.trim() || null,
        possible_source_asset_type_codes: form.sourceTypeCodes.length ? form.sourceTypeCodes : null,
        possible_destination_asset_type_codes: form.destTypeCodes.length ? form.destTypeCodes : null,
        aggregating: form.aggregating,
        source_cardinality: form.sourceCardinality,
        target_cardinality: form.targetCardinality,
        ordering: parseOrdering(form.ordering),
    }
}

/**
 * Backend guard: `^[a-z0-9_]{3,}$` (relation_type_service.go). The spec asks
 * for the slightly stricter `^[a-z][a-z0-9_]*$` (must start with a letter), so
 * the UI enforces the intersection of both.
 */
export const RELATION_TYPE_CODE_RE = /^[a-z][a-z0-9_]{2,}$/

export function isValidRelationTypeCode(code: string): boolean {
    return RELATION_TYPE_CODE_RE.test(code.trim())
}

/** The relation phrase built from the role labels: "member → group". */
export function relationPhrase(rt: AssetRelationType, locale: string): string | null {
    const source = getLocalizedName(rt.source_role, locale)
    const target = getLocalizedName(rt.target_role, locale)
    if (!source && !target) return null
    return `${source || '…'} → ${target || '…'}`
}

export function relationTypeLabel(rt: AssetRelationType, locale: string): string {
    return getLocalizedName(rt.name, locale) || rt.code
}
